import { useState, useEffect } from "react";
import { Car, Plus, X, Calendar } from "lucide-react";
import { T, seamBg } from "../theme";
import { Eyebrow, Card } from "../components";
import { createEvent, deleteEvent } from "../api/cloud";
import type { SyncEvent } from "../api/cloud";

function minToTime(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

export function DayPlan({ events, date }: { events: SyncEvent[]; date: string }) {
  const [showForm, setShowForm] = useState(false);
  const [label, setLabel] = useState("");
  const [startMin, setStartMin] = useState(600); // 10:00 AM default
  const [duration, setDuration] = useState(60);
  const [location, setLocation] = useState("");
  const [isFixed, setIsFixed] = useState(false);
  const [localEvents, setLocalEvents] = useState<SyncEvent[]>(events);

  // Sync with parent events when they change (e.g., after API fetch)
  useEffect(() => {
    setLocalEvents(events);
  }, [events]);

  // Sort events by start time
  const sorted = [...localEvents].sort((a, b) => (a.start_min ?? 0) - (b.start_min ?? 0));

  const handleAdd = async () => {
    const ev: SyncEvent = {
      date,
      label,
      start_min: startMin,
      duration_min: duration,
      location: location || undefined,
      is_fixed: isFixed,
    };
    try {
      const result = await createEvent(ev);
      setLocalEvents([...localEvents, { ...ev, id: result.id }]);
      setShowForm(false);
      setLabel("");
      setStartMin(600);
      setDuration(60);
      setLocation("");
      setIsFixed(false);
    } catch (e) {
      alert("Failed to save event. Are you online?");
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteEvent(id);
      setLocalEvents(localEvents.filter((e) => e.id !== id));
    } catch {
      alert("Failed to delete event");
    }
  };

  return (
    <div style={{ padding: "18px 16px 24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Eyebrow>day plan · {date}</Eyebrow>
        <button
          onClick={() => setShowForm(!showForm)}
          style={{
            background: "transparent",
            border: `1px solid ${T.edge}`,
            borderRadius: 8,
            padding: "4px 8px",
            color: T.mist,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
          }}
        >
          <Plus size={12} /> {showForm ? "Cancel" : "Add"}
        </button>
      </div>

      {showForm && (
        <Card style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            placeholder="Event label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{
              background: "rgba(0,0,0,0.3)",
              border: `1px solid ${T.edge}`,
              borderRadius: 6,
              padding: "6px 10px",
              color: T.paper,
              fontSize: 12,
              outline: "none",
            }}
          />
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <div className="mono" style={{ fontSize: 9, color: T.dim, marginBottom: 2 }}>START</div>
              <input
                type="time"
                value={`${Math.floor(startMin / 60).toString().padStart(2, "0")}:${(startMin % 60).toString().padStart(2, "0")}`}
                onChange={(e) => {
                  const [hStr, mStr] = e.target.value.split(":");
                  const h = Number(hStr ?? "0");
                  const m = Number(mStr ?? "0");
                  setStartMin(h * 60 + m);
                }}
                style={{
                  background: "rgba(0,0,0,0.3)",
                  border: `1px solid ${T.edge}`,
                  borderRadius: 6,
                  padding: "6px 10px",
                  color: T.paper,
                  fontSize: 12,
                  width: "100%",
                  outline: "none",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <div className="mono" style={{ fontSize: 9, color: T.dim, marginBottom: 2 }}>DURATION (min)</div>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                style={{
                  background: "rgba(0,0,0,0.3)",
                  border: `1px solid ${T.edge}`,
                  borderRadius: 6,
                  padding: "6px 10px",
                  color: T.paper,
                  fontSize: 12,
                  width: "100%",
                  outline: "none",
                }}
              />
            </div>
          </div>
          <input
            placeholder="Location (optional)"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            style={{
              background: "rgba(0,0,0,0.3)",
              border: `1px solid ${T.edge}`,
              borderRadius: 6,
              padding: "6px 10px",
              color: T.paper,
              fontSize: 12,
              outline: "none",
            }}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: T.mist, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={isFixed}
              onChange={(e) => setIsFixed(e.target.checked)}
              style={{ accentColor: T.gold }}
            />
            Fixed event (can't be moved)
          </label>
          <button
            onClick={handleAdd}
            disabled={!label}
            style={{
              background: label ? T.gold : T.dim,
              border: "none",
              borderRadius: 8,
              padding: "8px 12px",
              color: T.night,
              fontWeight: 600,
              fontSize: 12,
              cursor: label ? "pointer" : "not-allowed",
            }}
          >
            Save Event
          </button>
        </Card>
      )}

      <div style={{ marginTop: 16, position: "relative" }}>
        <div style={{ position: "absolute", left: 47, top: 6, bottom: 6, width: 2, background: T.edge }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sorted.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px 0", color: T.dim, fontSize: 12 }}>
              No events yet. Tap "Add" to create one.
            </div>
          )}
          {sorted.map((b) => (
            <div key={b.id} style={{ display: "flex", gap: 12, alignItems: "stretch" }}>
              <div className="mono" style={{ width: 38, fontSize: 10.5, color: T.dim, paddingTop: 9, textAlign: "right", fontWeight: 500 }}>
                {b.start_min ? minToTime(b.start_min) : "--"}
              </div>
              <div style={{ width: 10, position: "relative" }}>
                <div
                  style={{
                    position: "absolute",
                    left: 5,
                    top: 11,
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    transform: "translateX(-50%)",
                    background: b.location ? T.night : b.source === "calendar" ? T.dawn : b.is_fixed ? T.gold : T.mint,
                    border: b.location ? `2px solid ${T.mist}` : "none",
                  }}
                />
              </div>
              {b.location ? (
                <div style={{ flex: 1, borderRadius: 10, padding: "7px 12px", background: seamBg, border: `1px dashed ${T.edge}`, display: "flex", alignItems: "center", gap: 8 }}>
                  <Car size={12} color={T.mist} />
                  <span style={{ fontSize: 11.5, color: T.mist }}>{b.label}</span>
                  {b.location && <span className="mono" style={{ fontSize: 10, color: T.dim, marginLeft: "auto" }}>{b.location}</span>}
                </div>
              ) : (
                <div style={{ 
                  flex: 1, 
                  borderRadius: 10, 
                  padding: "9px 12px", 
                  background: T.panel, 
                  border: `1px solid ${T.edge}`, 
                  borderLeft: `3px solid ${b.source === "calendar" ? T.dawn : b.is_fixed ? T.gold : T.mint}` 
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: T.paper, fontWeight: 600 }}>{b.label}</span>
                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                      {b.source === "calendar" && (
                        <Calendar size={12} color={T.dim} />
                      )}
                      {b.source !== "calendar" && (
                        <button
                          onClick={() => b.id && handleDelete(b.id)}
                          style={{ background: "transparent", border: "none", color: T.dim, cursor: "pointer", padding: 2 }}
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mono" style={{ fontSize: 10, color: T.dim, marginTop: 2 }}>
                    {b.duration_min} min{b.is_fixed ? " · fixed" : ""}
                    {b.source === "calendar" && b.calendar_name ? ` · ${b.calendar_name}` : ""}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
